import { useState } from "react";
import { STAGE_META, autoCloseInDays, categoryLabel, formatStamp, visualStage } from "../../lib/complaints";
import { Icon } from "../msd/Icon";
import { Collapse, LinkBtn, Tag, LBL, cx } from "../msd/ui";
import ComplaintActions from "./ComplaintActions";
import ComplaintStageTracker from "./ComplaintStageTracker";
import ComplaintTimeline from "./ComplaintTimeline";

const STAGE_TONE = { logged: "brick", under_review: "amber", in_progress: "amber", resolved: "pine" };

export default function ComplaintCard({ row, complaint, config, canManage }) {
  const [expanded, setExpanded] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const stage = visualStage(complaint.stage);
  const meta = STAGE_META[stage];
  const autoCloseDays = autoCloseInDays(complaint, config?.auto_close_days ?? 3);

  return (
    <div
      className="overflow-hidden rounded-[14px] bg-msd-surface shadow-msd-e1"
      data-testid={`complaint-${complaint.complaint_no}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid={`complaint-toggle-${complaint.complaint_no}`}
        className="flex w-full flex-col gap-[10px] border-0 bg-transparent p-4 text-left text-msd-ink hover:bg-msd-soft"
      >
        <span className="flex flex-wrap items-center gap-2">
          <b>#{complaint.complaint_no}</b>
          <Tag tone="grey">{categoryLabel(complaint.category)}</Tag>
          {complaint.reopen_count > 0 && (
            <Tag tone="orange" icon="reset">
              Reopened ×{complaint.reopen_count}
            </Tag>
          )}
          <span className="ml-auto flex items-center gap-2">
            <Tag tone={STAGE_TONE[stage]}>{meta.label}</Tag>
            <span className="flex text-msd-faint">
              <Icon name={expanded ? "down" : "right"} size={16} stroke={2} />
            </span>
          </span>
        </span>
        <span className={cx("block text-[16px]", !expanded && "line-clamp-2")}>
          {complaint.summary || "No summary recorded."}
        </span>
        <span className={LBL}>Raised {formatStamp(complaint.raised_at)}</span>
      </button>

      <Collapse open={expanded}>
        <div className="flex flex-col gap-[10px] px-4 pb-4">
          <ComplaintStageTracker complaint={complaint} />

          {autoCloseDays !== null && (
            <p className={`m-0 ${LBL}`} data-testid={`complaint-auto-close-${complaint.complaint_no}`}>
              {autoCloseDays === 0
                ? "Auto-closes on the next check unless the customer says it's not fixed."
                : `Auto-closes in ${autoCloseDays} day${autoCloseDays === 1 ? "" : "s"} unless the customer says it's not fixed.`}
            </p>
          )}

          {canManage && (
            <ComplaintActions row={row} complaint={complaint} reopenWindowDays={config?.reopen_window_days ?? 7} />
          )}

          <LinkBtn
            onClick={() => setTimelineOpen((v) => !v)}
            aria-expanded={timelineOpen}
            data-testid={`complaint-timeline-toggle-${complaint.complaint_no}`}
            className="self-start font-medium !text-msd-ink2"
          >
            <Icon name={timelineOpen ? "down" : "right"} size={16} stroke={2} />
            Timeline
          </LinkBtn>

          <Collapse open={timelineOpen}>
            <ComplaintTimeline complaint={complaint} />
          </Collapse>
        </div>
      </Collapse>
    </div>
  );
}
